import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, UserIcon, LayersIcon, GraduationCapIcon, BuildingIcon, PenLineIcon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { PageTransition } from '../components/layout/PageTransition';
import { Animate, Stagger } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';
const SUBSTACK_URL = 'https://helgig.substack.com';
export function Home() {
  const {
    t
  } = useLanguage();
  const waysOfWorking = [{
    icon: <UserIcon size={28} data-id="element-1056" data-mp="2dhgrb1wthu9k" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    titleKey: 'home.ways.coaching.title',
    descKey: 'home.ways.coaching.desc',
    path: '/services/coaching'
  }, {
    icon: <LayersIcon size={28} data-id="element-1057" data-mp="30wrnkc0kxpdf" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    titleKey: 'home.ways.facilitation.title',
    descKey: 'home.ways.facilitation.desc',
    path: '/services/meetings-and-facilitation'
  }, {
    icon: <GraduationCapIcon size={28} data-id="element-1058" data-mp="2mxjpjmg5knat" />,
    color: 'bg-yellow-500',
    lightColor: 'bg-yellow-100',
    textColor: 'text-yellow-600',
    borderColor: 'border-yellow-200',
    titleKey: 'home.ways.training.title',
    descKey: 'home.ways.training.desc',
    path: '/services/courses'
  }, {
    icon: <BuildingIcon size={28} data-id="element-1059" data-mp="2895nveuh34vu" />,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    titleKey: 'home.ways.embedded.title',
    descKey: 'home.ways.embedded.desc',
    path: '/services/embedded-partner'
  }];
  const writingPreviews = [{
    series: t('home.writing.series1Label'),
    title: t('home.writing.series1Title'),
    excerpt: t('home.writing.series1Excerpt'),
    href: SUBSTACK_URL,
    color: 'border-coral/30',
    labelColor: 'text-coral',
    labelBg: 'bg-coral/10'
  }, {
    series: t('home.writing.series2Label'),
    title: t('home.writing.series2Title'),
    excerpt: t('home.writing.series2Excerpt'),
    href: SUBSTACK_URL,
    color: 'border-teal/30',
    labelColor: 'text-teal',
    labelBg: 'bg-teal/10'
  }, {
    series: t('home.writing.series3Label'),
    title: t('home.writing.series3Title'),
    excerpt: t('home.writing.series3Excerpt'),
    href: SUBSTACK_URL,
    color: 'border-yellow-400/40',
    labelColor: 'text-yellow-700',
    labelBg: 'bg-yellow-100'
  }];
  return <PageTransition data-id="element-1060" data-mp="2macge4euhvfw">
      {/* Hero */}
      <section className="relative pt-20 pb-32 overflow-hidden" data-id="element-1061" data-mp="2m8hxswr1qfez">
        <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[600px] h-[600px] translate-x-1/3 -translate-y-1/4" data-id="element-1062" data-mp="2r1az1anb35mb" />
        <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-[400px] h-[400px] -translate-x-1/4 translate-y-1/4" delay={0.5} data-id="element-1063" data-mp="fk983fq9mpl0" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1064" data-mp="oenlfcr8jq1h">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1065" data-mp="kcisyhciwzgf">
            <Stagger staggerDelay={0.15} data-id="element-1066" data-mp="ken67pwbbatd">
              <div className="inline-block px-4 py-2 bg-teal/10 text-teal-dark rounded-full font-bold text-sm mb-6" data-id="element-1067" data-mp="3sru7rk1fb4i6">
                {t('home.hero.badge')}
              </div>
              <h1 className="md:text-6xl lg:text-7xl font-heading font-extrabold text-charcoal mb-6 text-[52px] leading-tight" data-id="element-1068" data-mp="5nmosvdl86l3">
                {t('home.hero.titleLine1')}<br data-id="element-1069" data-mp="n01jese5f8p" />
                <span className="text-coral" data-id="element-1070" data-mp="qnw8h9ko7p0">{t('home.hero.titleLine2')}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-4 leading-relaxed max-w-lg" data-id="element-1071" data-mp="1h4w4r7xicghn">
                {t('home.hero.description')}
              </p>
              <p className="text-base text-gray-500 mb-8 leading-relaxed max-w-lg" data-id="element-1072" data-mp="27eoh61l3lep7">
                {t('home.hero.subDescription')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1073" data-mp="2cf2eaol5v48g">
                <Link to="/contact" data-id="element-1074" data-mp="2hb17cphfpaf7">
                  <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1076" data-mp="2maw2544byk5j" />} data-id="element-1075" data-mp="2he5m2gsizok4">
                    {t('home.hero.cta')}
                  </Button>
                </Link>
                <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" data-id="element-1077" data-mp="2qbzgtnan6a56">
                  <Button variant="outline" size="lg" rightIcon={<ExternalLinkIcon size={16} data-id="element-1079" data-mp="2v9r5m8fzpg55" />} data-id="element-1078" data-mp="2vb2dajck381j">
                    {t('home.hero.ctaWriting')}
                  </Button>
                </a>
              </div>
            </Stagger>

            <Animate delay={0.2} className="relative hidden lg:block" data-id="element-1080" data-mp="350vkl85o10zz">
              <div className="relative w-full aspect-square max-w-md mx-auto" data-id="element-1081" data-mp="3a1rmxhadcf3a">
                <div className="absolute inset-0 bg-coral rounded-[3rem] rotate-6 opacity-20" data-id="element-1082" data-mp="3e1trpzbehoms" />
                <div className="absolute inset-0 bg-teal rounded-[3rem] -rotate-6 opacity-20" data-id="element-1083" data-mp="12fhuj5itnb7j" />
                <img src="https://cdn.magicpatterns.com/uploads/6bt3PcPytwUVW4EyeZ9z5f/Helgi-6_A_height_x30_copy.jpg" alt="Helgi Gudmundsson" className="relative w-full h-full object-cover rounded-[3rem] shadow-2xl" loading="eager" decoding="async" fetchPriority="high" data-id="element-1084" data-mp="ycv9bngnoojb" />
                <div className="absolute -bottom-8 -left-8 bg-white p-4 rounded-2xl shadow-xl flex items-center gap-3" data-id="element-1085" data-mp="1h44g3oz1kz77">
                  <div className="bg-teal/10 p-2 rounded-full" data-id="element-1086" data-mp="1cc4cnmjrjo5v">
                    <PenLineIcon size={22} className="text-teal" data-id="element-1087" data-mp="17e2tl1mc7oue" />
                  </div>
                  <div data-id="element-1088" data-mp="17c8hmct1n72g">
                    <p className="font-bold text-charcoal text-sm" data-id="element-1089" data-mp="24tyvad6u2zum">{t('home.hero.substackBadge')}</p>
                    <p className="text-xs text-gray-500" data-id="element-1090" data-mp="2028epphazfur">{t('home.hero.substackSub')}</p>
                  </div>
                </div>
              </div>
            </Animate>
          </div>
        </div>
      </section>

      {/* Writing section */}
      <section className="py-24 bg-cream relative" data-id="element-1091" data-mp="2609m3aabqcv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1092" data-mp="3klaw70ns5hqf">
          <Animate className="mb-12" data-id="element-1093" data-mp="3kiye8ni0v7dd">
            <span className="text-teal font-bold tracking-wider uppercase text-sm" data-id="element-1094" data-mp="3pj2k7jlb0jpc">
              {t('home.writing.sectionLabel')}
            </span>
            <h2 className="text-4xl font-bold mt-2 mb-3" data-id="element-1095" data-mp="3aopr6fbrg6ml">
              {t('home.writing.title')}
            </h2>
            <p className="text-gray-500 max-w-xl" data-id="element-1096" data-mp="31lwws37a7esj">
              {t('home.writing.subtitle')}
            </p>
          </Animate>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-1097" data-mp="36k870f1hnrba">
            {writingPreviews.map((item, i) => <Animate key={i} delay={i * 0.1} data-id="element-1098" data-mp="zvv84dfy474h" data-mpi={i}>
                <a href={item.href} target="_blank" rel="noopener noreferrer" className="block group h-full" data-id="element-1099" data-mp="14tmxazp7hf4z">
                  <Card className={`h-full p-7 border-2 ${item.color} hover:shadow-md transition-all`} hoverEffect data-id="element-1100" data-mp="1exbqx8uzaav6">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide mb-4 ${item.labelBg} ${item.labelColor}`} data-id="element-1101" data-mp="19za1m66ylfb9">
                      {item.series}
                    </span>
                    <h3 className="text-lg font-bold text-charcoal mb-3 group-hover:text-coral transition-colors leading-snug" data-id="element-1102" data-mp="1iydlvdhv40rc">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-5" data-id="element-1103" data-mp="1snx5wmhba0ns">
                      {item.excerpt}
                    </p>
                    <div className="flex items-center text-sm font-bold text-gray-400 group-hover:text-coral transition-colors" data-id="element-1104" data-mp="26mn8uiqtauk2">
                      {t('home.writing.readOn')}
                      <ExternalLinkIcon size={13} className="ml-2" data-id="element-1105" data-mp="cb8hxym2ep2q" />
                    </div>
                  </Card>
                </a>
              </Animate>)}
          </div>

          <Animate delay={0.35} className="text-center" data-id="element-1106" data-mp="wtjwo3cfng6i">
            <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" data-id="element-1107" data-mp="11swfmd06o33t">
              <Button variant="outline" rightIcon={<ExternalLinkIcon size={16} data-id="element-1109" data-mp="16jmnpxzzuk27" />} data-id="element-1108" data-mp="11qtlphvwtptt">
                {t('home.writing.viewAll')}
              </Button>
            </a>
          </Animate>
        </div>
      </section>

      {/* Ways of Working */}
      <section className="py-24 relative overflow-hidden" data-id="element-1110" data-mp="dw8wmgiz8iq0">
        <DecorativeShape type="square" color="bg-coral/5" className="top-20 left-0 w-64 h-64 -rotate-12" data-id="element-1111" data-mp="iuahn6zyqs44" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1112" data-mp="mw5gm4uf8teq">
          <Animate className="mb-12" data-id="element-1113" data-mp="my02pmhfirke">
            <span className="text-teal font-bold tracking-wider uppercase text-sm" data-id="element-1114" data-mp="rvi1wl5n9sf7">
              {t('home.ways.sectionLabel')}
            </span>
            <h2 className="text-4xl font-bold mt-2 mb-2" data-id="element-1115" data-mp="1yaj3b6dfv2t6">
              {t('home.ways.title')}
            </h2>
            <p className="text-gray-500 max-w-xl" data-id="element-1116" data-mp="23c8d6ioxdafo">{t('home.ways.subtitle')}</p>
          </Animate>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1117" data-mp="2dg6mnmm12wxu">
            {waysOfWorking.map((way, i) => <Animate key={i} delay={i * 0.1} data-id="element-1118" data-mp="28hbwfzlqe63p" data-mpi={i}>
                <Link to={way.path} className="block group h-full" data-id="element-1119" data-mp="23n7obd0qbs11">
                  <Card className={`h-full p-6 border-2 ${way.borderColor} hover:shadow-md transition-all`} hoverEffect data-id="element-1120" data-mp="1zmxetqdqajhf">
                    <div className="flex items-start gap-4" data-id="element-1121" data-mp="1unukazvdjvnp">
                      <div className={`p-3 rounded-2xl ${way.lightColor} flex-shrink-0`} data-id="element-1122" data-mp="1poho3oim5c8g">
                        <div className={way.textColor} data-id="element-1123" data-mp="1pquf0r6cmwxn">{way.icon}</div>
                      </div>
                      <div className="flex-1" data-id="element-1124" data-mp="1kr82yl29bmqf">
                        <h3 className={`text-lg font-bold text-charcoal mb-2 group-hover:${way.textColor} transition-colors`} data-id="element-1125" data-mp="1gouz6272lywa">
                          {t(way.titleKey)}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-1126" data-mp="3b2vvogf5e9t7">
                          {t(way.descKey)}
                        </p>
                        <div className={`flex items-center ${way.textColor} text-sm font-bold`} data-id="element-1127" data-mp="cqfrbuplixiu">
                          {t('home.ways.learnMore')}
                          <ArrowRightIcon size={14} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1128" data-mp="mlg5b0eif7tk" />
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              </Animate>)}
          </div>

          <Animate delay={0.4} className="mt-8 text-center" data-id="element-1129" data-mp="3l5g47hg9lzlz">
            <Link to="/services" data-id="element-1130" data-mp="3nn2dy3vyxw6">
              <Button variant="outline" rightIcon={<ArrowRightIcon size={16} data-id="element-1132" data-mp="3pzuh43cyomy" />} data-id="element-1131" data-mp="3oyecl7hjbyd">
                {t('home.ways.viewAll')}
              </Button>
            </Link>
          </Animate>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-charcoal text-white relative overflow-hidden rounded-t-[3rem] mx-4 sm:mx-8 mb-8" data-id="element-1133" data-mp="37b5g92c3e9gz">
        <DecorativeShape type="donut" color="border-white/10" className="top-0 right-0 w-96 h-96 translate-x-1/3 -translate-y-1/3" data-id="element-1134" data-mp="32c2lqbtqnln9" />
        <DecorativeShape type="blob" color="bg-coral" className="bottom-0 left-0 w-64 h-64 -translate-x-1/3 translate-y-1/3 blur-3xl opacity-50" data-id="element-1135" data-mp="1mmupi8iim6ss" />
        <Animate className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-1136" data-mp="18t3f6xwymiw0">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1137" data-mp="1cxb691qw4axe">{t('home.cta.title')}</h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-1138" data-mp="20nnnj380zk0n">{t('home.cta.description')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1139" data-mp="25k5pxwfvv69t">
            <Link to="/contact" data-id="element-1140" data-mp="1rmh3ik663r2n">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" data-id="element-1141" data-mp="1rl5yyi4wefah">{t('home.cta.bookCall')}</Button>
            </Link>
            <Link to="/about" data-id="element-1142" data-mp="1wmlmqhygmzwk">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto" data-id="element-1143" data-mp="g5zf97cq4ur3">{t('home.cta.readAbout')}</Button>
            </Link>
          </div>
        </Animate>
      </section>
    </PageTransition>;
}